import { SxProps, Theme, TypographyProps } from '@mui/material';

// shadow Styles
export const SHADOWS = {
  primary: {
    boxShadow: 'rgba(100, 100, 111, 0.15) 0px 7px 29px 0px',
  },
  secondary: {
    boxShadow: 'rgba(0, 0, 0, 0.07) 0px 3px 12px',
  },
};

const ROUNDING = {
  primary: {
    borderRadius: 3.2,
  },
  secondary: {
    borderRadius: 2.5,
  },
};

export const defaultContainerStyling = {
  ...SHADOWS.primary,
  ...ROUNDING.primary,
};

export const secondaryContainerStyling = {
  ...SHADOWS.secondary,
  ...ROUNDING.secondary,
};

// Sign In page styles
export const authContainerStyling: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: { xs: 'start', sm: 'center' },
  height: '100vh',
  overflow: 'auto',
  px: { xs: 0, sm: 4 },
};
export const authSecondaryContainerStyling: SxProps = {
  ...secondaryContainerStyling,
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  overflow: 'auto',
  backgroundColor: 'white',
  p: 2,
  height: { xs: '100vh', sm: 'auto' },
};
export const authHeaderContainerStyling: SxProps = {
  width: { xs: '100%', md: '80%' },
  p: { xs: 3, md: 4 },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  textAlign: { xs: 'center', md: 'left' },
  alignItems: { xs: 'center', md: 'flex-start' },
  borderRadius: 3.2,
  gap: { xs: 5, md: 0 },
};
export const authHeaderTypographyStyling: SxProps = {
  fontSize: { xs: '1.5rem', md: '2rem' },
  fontWeight: 'medium',
};
export const authLogoStyling: SxProps = { width: { xs: 120, md: 160 } };
export const authFormPrimaryContainerStyling: SxProps = {
  width: '100%',
  backgroundColor: 'white',
  px: { xs: 0, md: 4 },
  py: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
export const authFormSecondaryContainerStyling: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};
export const authFormHeaderStyling: SxProps = { display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 2 };
export const externalFormStyling: SxProps = { mt: 1, display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '90%', sm: '80%' } };
export const createAccountContainerStyling: SxProps = {
  marginTop: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  px: { xs: 1, md: 4 },
  py: 4,
};

export const headerProps: SxProps = {
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'center',
  justifyContent: 'space-between',
  gap: 1,
  my: 2,
  position: 'sticky',
};
export const headerTypographyProps: TypographyProps = {
  variant: 'h5',
  fontWeight: '600',
  lineHeight: '1.5',
  fontSize: { xs: '1.25rem', sm: '1.5rem' },
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
};
export const inputContainerStyling: SxProps = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr 1fr',
  },
  gap: { xs: 0, sm: 2 },
  overflow: 'auto',
  width: '100%',
};
export const inputDividerStyling: SxProps = { display: 'flex', flexDirection: 'column', width: '100%', minWidth: 200, gap: 2, pt: { xs: 2, sm: 1 } };
export const listPageTableRowStyling: SxProps = {
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  whiteSpace: 'nowrap',
  overflow: 'auto',
};
export const itemHeaderStyling: SxProps = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 1,
  alignContent: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  left: 0,
  right: 0,
  bgcolor: 'background.paper',
  zIndex: 10,
  p: 2,
  border: '1px solid var(--border-color)',
  borderRadius: 1,
  boxShadow: '0px 10px 10px rgba(0, 0, 0, 0.08)',
};
export const dialogActionsStyles: SxProps = { display: 'flex', justifyContent: 'space-between' };

export const getListPageMaxHeight = () => 'calc(100vh - var(--menu-bar-height) - 100px)';

export const listPageLayoutStyles: SxProps = { display: { md: 'flex' }, my: 2, alignItems: 'flex-start', gap: 2 };
export const listPageTableWrapperStyles: SxProps = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: getListPageMaxHeight(),
  width: '100%',
  overflow: 'hidden',
};
export const stickyHeaderCellSx = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: 'var(--table-header-bg)',
};
export const listPagePaginationStyles: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 1,
};
export const itemWrapperStyling: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
  mx: 'auto',
};

export const itemInnerLayoutStyling: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', lg: 'row' },
  alignItems: 'flex-start',
  gap: { xs: 0, lg: 2 },
};

export const itemContainerStyling: SxProps<Theme> = {
  flex: '1 1 0px',
  minWidth: 0,
  width: '100%',
  maxWidth: 'unset',
};

export const itemActivitySectionStyling: SxProps<Theme> = {
  minWidth: { lg: '400px' },
  width: '100%',
  flex: { lg: '0 0 clamp(420px, 28vw, 520px)' },
  maxWidth: {
    xs: '100%',
  },
  alignSelf: 'stretch',
};

export const EventToggleBtnStyling: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',

  '& .MuiToggleButton-root': {
    borderRadius: 1.5,
    px: 2,
    py: 1,
    gap: 1,
    textTransform: 'none',
    fontWeight: 500,
    color: 'text.secondary',
    border: '1px solid transparent',
    '& svg': {
      fontSize: 18,
    },

    '&.Mui-selected': {
      backgroundColor: 'transparent',
      color: 'primary.main',
      border: '1px solid',
      borderColor: 'primary.main',

      '&:hover': {
        backgroundColor: 'action.hover',
      },
    },
  },
};

export const secondaryTableWrapperSx: SxProps = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiTableContainer-root': {
    maxHeight: 400,
  },
  '& td:first-of-type, & th:first-of-type': {
    paddingLeft: 2,
  },
};
export const accordionDetailsContainerSx: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: { xs: 0, sm: 2 },
  flexDirection: { xs: 'column', sm: 'row' },
  overflow: 'auto',
};

export const multilineTextSx = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};
